import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { PageLoad } from '../components/ui';
import { usePrograms } from '../context/ProgramsContext';

const AMBER = '#F5A623';

const STATUS_CFG = {
  pre_req:    { label: 'Pre-Req',    color: '#5C7388', bg: '#EEF1F5', dot: '#5C7388', bar: '#9AAAB9' },
  ready:      { label: 'Ready',      color: '#B47200', bg: '#FBF1DD', dot: '#F5A623', bar: '#F5A623' },
  active:     { label: 'Active',     color: '#0F6CBD', bg: '#E5F0FB', dot: '#1F87E5', bar: '#1F87E5' },
  completing: { label: 'Completing', color: '#6B41B6', bg: '#EFE7F8', dot: '#8A5BD0', bar: '#8A5BD0' },
  complete:   { label: 'Complete',   color: '#1F8A5B', bg: '#E6F3EC', dot: '#1F8A5B', bar: '#1F8A5B' },
};

const PROG_COLORS = ['#1F87E5', '#F5A623', '#8A5BD0', '#1F8A5B', '#D14343', '#0F6CBD', '#D9892A', '#5C7388'];

/* ═══════════════════════════════════════
   Root
═══════════════════════════════════════ */
export default function DashboardPage() {
  const { user } = useAuth();
  return user?.role === 'admin' ? <AdminDashboard /> : <PCDashboard />;
}

/* ═══════════════════════════════════════
   Admin Dashboard
═══════════════════════════════════════ */
function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { programMap: PROGRAM_NAMES } = usePrograms();

  const [students, setStudents] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [cohortDates, setCohortDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pcFilter, setPcFilter] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/students'),
      api.get('/hosts'),
      api.get('/reports/summary'),
      api.get('/reports/recent-activity').catch(() => ({ data: [] })),
      api.get('/reports/cohort-dates').catch(() => ({ data: [] })),
    ]).then(([stRes, hRes, smRes, actRes, cdRes]) => {
      setStudents(stRes.data);
      setHosts(hRes.data);
      setSummary(smRes.data);
      setActivity(actRes.data || []);
      setCohortDates(cdRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const firstName = user?.full_name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const pcTabs = useMemo(() => {
    const map = {};
    students.forEach(s => {
      if (s.assigned_pc_id && s.pc_name) {
        if (!map[s.assigned_pc_id]) map[s.assigned_pc_id] = { id: s.assigned_pc_id, name: s.pc_name, count: 0 };
        map[s.assigned_pc_id].count++;
      }
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  const displayStudents = useMemo(() => {
    if (!pcFilter) return students;
    return students.filter(s => s.assigned_pc_id === pcFilter);
  }, [students, pcFilter]);

  const total      = displayStudents.length;
  const active     = displayStudents.filter(s => s.status === 'active').length;
  const completing = displayStudents.filter(s => s.status === 'completing').length;
  const complete   = displayStudents.filter(s => s.status === 'complete').length;
  const totalHours = displayStudents.reduce((acc, s) => acc + (parseFloat(s.hours_logged) || 0), 0);
  const placed     = active + completing + complete;
  const rate       = pcFilter
    ? (placed > 0 ? Math.round(complete / placed * 100) : 0)
    : (summary?.completionRate90Days || 0);

  const statusOrder = ['pre_req', 'ready', 'active', 'completing', 'complete'];
  const byStatus = statusOrder.map(key => ({
    key, ...STATUS_CFG[key],
    count: displayStudents.filter(s => s.status === key).length,
  }));
  const maxStatus = Math.max(...byStatus.map(s => s.count), 1);

  const placementTrend = useMemo(() => {
    const weeks = [];
    const now = new Date();
    for (let w = 11; w >= 0; w--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - w * 7 - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);
      const placedW = displayStudents.filter(s => {
        if (!s.practicum_start_date) return false;
        const d = new Date(s.practicum_start_date);
        return d >= weekStart && d < weekEnd;
      }).length;
      const completed = displayStudents.filter(s => {
        if (!s.practicum_start_date || s.status !== 'complete') return false;
        const d = new Date(s.practicum_start_date);
        return d >= weekStart && d < weekEnd;
      }).length;
      weeks.push({ w: `W${12 - w}`, placed: placedW, completed });
    }
    return weeks;
  }, [displayStudents]);

  const programSplit = useMemo(() => {
    const map = {};
    displayStudents.forEach(s => {
      const code = s.program_code || s.program_id || 'unknown';
      const name = PROGRAM_NAMES[code] || s.program_name || code;
      if (!map[code]) map[code] = { name, code, students: 0, placements: 0 };
      map[code].students++;
      if (s.status === 'complete') map[code].placements++;
    });
    return Object.values(map)
      .filter(p => p.students > 0)
      .sort((a, b) => b.students - a.students)
      .slice(0, 6)
      .map((p, i) => ({ ...p, color: PROG_COLORS[i % PROG_COLORS.length] }));
  }, [displayStudents, PROGRAM_NAMES]);

  const tasks = useMemo(() => {
    const list = [];
    const readyNoHost = displayStudents.filter(s => s.status === 'ready' && !s.host_id);
    if (readyNoHost.length > 0)
      list.push({ id: 'match', type: 'placement', title: `Match ${readyNoHost.length} ready student${readyNoHost.length > 1 ? 's' : ''} to a host`, subtitle: 'No placement assigned yet', priority: 'high', href: '/students' });

    if (!pcFilter) {
      const pendingAgreements = hosts.filter(h => ['sent', 'host_signed'].includes(h.agreement_status));
      if (pendingAgreements.length > 0)
        list.push({ id: 'agree', type: 'agreement', title: `${pendingAgreements.length} agreement${pendingAgreements.length > 1 ? 's' : ''} awaiting signature`, subtitle: pendingAgreements.slice(0, 2).map(h => h.org_name).join(', ') + (pendingAgreements.length > 2 ? ` +${pendingAgreements.length - 2} more` : ''), priority: 'high', href: '/hosts' });

      const expiring = hosts.filter(h => {
        if (!h.agreement_expires_date) return false;
        const days = Math.ceil((new Date(h.agreement_expires_date) - new Date()) / 86400000);
        return days > 0 && days <= 30;
      });
      if (expiring.length > 0)
        list.push({ id: 'expire', type: 'agreement', title: `${expiring.length} agreement${expiring.length > 1 ? 's' : ''} expiring within 30 days`, subtitle: expiring.slice(0, 2).map(h => h.org_name).join(', '), priority: 'med', href: '/hosts' });
    }

    return list.slice(0, 6);
  }, [displayStudents, hosts, pcFilter]);

  const hostsAttention = useMemo(() => {
    if (pcFilter) return [];
    const list = [];
    const now = new Date();
    hosts.filter(h => h.is_active && h.agreement_expires_date).forEach(h => {
      const days = Math.ceil((new Date(h.agreement_expires_date) - now) / 86400000);
      if (days > 0 && days <= 21)
        list.push({ name: h.org_name, reason: `Agreement expires in ${days} day${days !== 1 ? 's' : ''}`, level: days <= 7 ? 'red' : 'amber', id: h.id });
    });
    hosts.filter(h => h.agreement_status === 'host_signed').forEach(h => {
      list.push({ name: h.org_name, reason: 'Awaiting Dean counter-signature', level: 'amber', id: h.id });
    });
    hosts.filter(h => h.is_active && h.agreement_status === 'none').slice(0, 3).forEach(h => {
      list.push({ name: h.org_name, reason: 'No site agreement on file', level: 'amber', id: h.id });
    });
    return list.slice(0, 5);
  }, [hosts, pcFilter]);

  if (loading) return <PageLoad />;

  const highPriorityCount = tasks.filter(t => t.priority === 'high').length + hostsAttention.filter(h => h.level === 'red').length;

  return (
    <>
      <DashHero
        greeting={greeting} firstName={firstName} dateStr={dateStr}
        total={total} active={active} completing={completing} complete={complete}
        totalHours={totalHours} rate={rate} hosts={hosts}
        highPriorityCount={highPriorityCount}
        pcTabs={pcTabs} pcFilter={pcFilter} setPcFilter={setPcFilter}
      />

      <div className="h-content">
        <div className="h-content-inner">
          <div className="dash-grid-new">

            <div className="dash-col-main">
              <Widget
                title="Placement trend"
                sub="Placements started vs. completions · last 12 weeks"
                action={<Link to="/reports" className="wgt-link">Detailed report <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></Link>}
              >
                <PlacementChart data={placementTrend} />
              </Widget>

              <Widget
                title="All students" sub="Latest cohort activity"
                action={<Link to="/students" className="wgt-link">View all <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></Link>}
                noPad
              >
                <DashStudentTable students={students.slice(0, 8)} navigate={navigate} PROGRAM_NAMES={PROGRAM_NAMES} />
              </Widget>

              <Widget title="Students by program" sub="Completions vs. enrolment">
                <DashProgramSplit programs={programSplit} />
              </Widget>
            </div>

            <div className="dash-col-side">
              <Widget
                title="Today"
                sub={`${tasks.length} item${tasks.length !== 1 ? 's' : ''} · ${tasks.filter(t => t.priority === 'high').length} high priority`}
                action={<Link to="/students" className="wgt-link">Students <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></Link>}
              >
                {tasks.length > 0 ? (
                  <DashTasks tasks={tasks} navigate={navigate} />
                ) : (
                  <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No outstanding items — all clear!</p>
                )}
              </Widget>

              <Widget title="Students by status" sub={`${total} total`}>
                <DashStatusBreakdown byStatus={byStatus} max={maxStatus} />
              </Widget>

              {hostsAttention.length > 0 && (
                <Widget title="Hosts needing attention" sub={`${hostsAttention.length} partner${hostsAttention.length !== 1 ? 's' : ''} flagged`}>
                  <DashHostsAttention items={hostsAttention} navigate={navigate} />
                </Widget>
              )}

              <Widget title="Upcoming" sub="This week">
                <DashUpcoming dates={cohortDates} students={students} />
              </Widget>

              <Widget title="Recent activity">
                <DashActivity items={activity} />
              </Widget>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════
   PC Dashboard
═══════════════════════════════════════ */
function PCDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { programMap: PROGRAM_NAMES } = usePrograms();
  const [students, setStudents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/students'),
      api.get('/reports/recent-activity').catch(() => ({ data: [] })),
    ]).then(([stRes, actRes]) => {
      setStudents(stRes.data);
      setActivity(actRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const firstName = user?.full_name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const total      = students.length;
  const active     = students.filter(s => s.status === 'active').length;
  const completing = students.filter(s => s.status === 'completing').length;
  const complete   = students.filter(s => s.status === 'complete').length;

  const statusOrder = ['pre_req', 'ready', 'active', 'completing', 'complete'];
  const byStatus = statusOrder.map(key => ({
    key, ...STATUS_CFG[key],
    count: students.filter(s => s.status === key).length,
  }));
  const maxStatus = Math.max(...byStatus.map(s => s.count), 1);

  // Placement trend for this coordinator's students — placements started vs completions, last 12 weeks
  const placementTrend = useMemo(() => {
    const weeks = [];
    const now = new Date();
    for (let w = 11; w >= 0; w--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - w * 7 - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);
      const placedW = students.filter(s => {
        if (!s.practicum_start_date) return false;
        const d = new Date(s.practicum_start_date);
        return d >= weekStart && d < weekEnd;
      }).length;
      const completed = students.filter(s => {
        if (!s.practicum_start_date || s.status !== 'complete') return false;
        const d = new Date(s.practicum_start_date);
        return d >= weekStart && d < weekEnd;
      }).length;
      weeks.push({ w: `W${12 - w}`, placed: placedW, completed });
    }
    return weeks;
  }, [students]);

  const alerts = useMemo(() => {
    const list = [];
    return list.slice(0, 5);
  }, [students]);

  if (loading) return <PageLoad />;

  return (
    <>
      <div className="h-hero">
        <div className="h-hero-decor" aria-hidden="true">
          <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
            <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
              <circle cx="720" cy="160" r="80"/><circle cx="720" cy="160" r="140"/>
              <circle cx="720" cy="160" r="200"/><circle cx="720" cy="160" r="260"/>
            </g>
            <g fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1">
              <circle cx="540" cy="40" r="180"/><circle cx="540" cy="40" r="240"/>
            </g>
          </svg>
        </div>
        <div className="h-hero-inner">
          <div className="h-hero-top">
            <div>
              <div className="h-hero-crumb">
                <span>{dateStr}</span><span className="h-crumb-sep">·</span>
                <span>Career Services Dashboard</span>
              </div>
              <h1 className="h-hero-title">
                {greeting}, <span className="h-hero-accent">{firstName}</span>.
              </h1>
              <p className="h-hero-lede">{total} students assigned · {active + completing} on active placement.</p>
            </div>
          </div>
          <div className="h-stats dash-stats-4" style={{ gridTemplateColumns: 'repeat(3,1fr)', maxWidth: 700 }}>
            <DashKpiCard eyebrow="MY STUDENTS" value={total} label="Total assigned" delta={`${active} active`} fill={total ? Math.round(active / total * 100) : 0} icon="students" />
            <DashKpiCard eyebrow="ACTIVE" value={active + completing} label="On placement now" delta={`${completing} completing`} fill={total ? Math.round((active + completing) / total * 100) : 0} icon="doc" />
            <DashKpiCard eyebrow="COMPLETE" value={complete} label="Finished this term" delta={`${alerts.length} alert${alerts.length !== 1 ? 's' : ''}`} fill={total ? Math.round(complete / total * 100) : 0} icon="reports" />
          </div>
        </div>
      </div>

      <div className="h-content">
        <div className="h-content-inner">
          <div className="dash-grid-new">
            <div className="dash-col-main">
              <Widget title="Placement trend" sub="Placements started vs. completions · last 12 weeks">
                <PlacementChart data={placementTrend} />
              </Widget>
              <Widget title={`My Students (${total})`} sub="All assigned students"
                action={<Link to="/students" className="wgt-link">View all <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></Link>}
                noPad
              >
                <DashStudentTable students={students.slice(0, 10)} navigate={navigate} PROGRAM_NAMES={PROGRAM_NAMES} />
              </Widget>
            </div>
            <div className="dash-col-side">
              {alerts.length > 0 && (
                <Widget title="Action needed" sub={`${alerts.length} item${alerts.length !== 1 ? 's' : ''}`}>
                  <DashTasks tasks={alerts} navigate={navigate} />
                </Widget>
              )}
              <Widget title="Students by status" sub={`${total} total`}>
                <DashStatusBreakdown byStatus={byStatus} max={maxStatus} />
              </Widget>
              <Widget title="Recent activity">
                <DashActivity items={activity} />
              </Widget>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════
   Hero component
═══════════════════════════════════════ */
function DashHero({ greeting, firstName, dateStr, total, active, completing, complete, totalHours, rate, hosts, highPriorityCount, pcTabs, pcFilter, setPcFilter }) {
  const totalHoursStr = totalHours >= 1000 ? `${(totalHours / 1000).toFixed(1)}k` : String(Math.round(totalHours));
  const activeHosts = hosts.filter(h => h.is_active).length;
  const selectedPc = pcTabs?.find(p => p.id === pcFilter);

  return (
    <div className="h-hero">
      <div className="h-hero-decor" aria-hidden="true">
        <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
          <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
            <circle cx="720" cy="160" r="80"/><circle cx="720" cy="160" r="140"/>
            <circle cx="720" cy="160" r="200"/><circle cx="720" cy="160" r="260"/>
          </g>
          <g fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1">
            <circle cx="540" cy="40" r="180"/><circle cx="540" cy="40" r="240"/>
          </g>
        </svg>
      </div>
      <div className="h-hero-inner">
        <div className="h-hero-top">
          <div>
            <div className="h-hero-crumb">
              <span>{dateStr}</span><span className="h-crumb-sep">·</span>
              <span>Career Services Dashboard</span>
            </div>
            <h1 className="h-hero-title">
              {greeting}, <span className="h-hero-accent">{firstName}</span>.
            </h1>
            <p className="h-hero-lede">
              {selectedPc
                ? `Showing ${total} student${total !== 1 ? 's' : ''} assigned to ${selectedPc.name} · ${active + completing} on active placement`
                : `${total} students across active programs · ${active + completing} on active placement`}
              {highPriorityCount > 0 && !pcFilter ? ` · ${highPriorityCount} item${highPriorityCount !== 1 ? 's' : ''} need your attention` : ''}.
            </p>
          </div>
          <div className="h-hero-actions">
            <Link to="/students" className="h-btn-ghost" style={{ textDecoration: 'none' }}>
              <DI d={<g><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="7" r="2.5"/><path d="M16 14c2.8 0 5 2.2 5 5"/></g>} size={16} /> View All Students
            </Link>
            <Link to="/students" className="h-btn-accent" style={{ background: AMBER, textDecoration: 'none', color: '#0B1F2E' }}>
              <DI d="M12 5v14M5 12h14" size={16} /> New Student
            </Link>
          </div>
        </div>

        {pcTabs && pcTabs.length > 0 && (
          <div className="dash-pc-tabs">
            <button
              className={`dash-pc-tab${!pcFilter ? ' active' : ''}`}
              onClick={() => setPcFilter(null)}
            >
              All coordinators
            </button>
            {pcTabs.map(pc => (
              <button
                key={pc.id}
                className={`dash-pc-tab${pcFilter === pc.id ? ' active' : ''}`}
                onClick={() => setPcFilter(pcFilter === pc.id ? null : pc.id)}
              >
                {pc.name}
                <span className="dash-pc-tab-count">{pc.count}</span>
              </button>
            ))}
          </div>
        )}

        <div className="h-stats dash-stats-4">
          <DashKpiCard eyebrow="ACTIVE" value={active + completing} label="Students on placement" delta={`+${completing} completing`} fill={total ? Math.round((active + completing) / total * 100) : 0} icon="students" />
          <DashKpiCard eyebrow="COMPLETION · 90D" value={`${rate}%`} label="On-track this term" delta={pcFilter ? 'completions vs. placed' : 'based on 90-day window'} fill={parseFloat(rate)} icon="reports" />
          <DashKpiCard eyebrow="HOURS LOGGED" value={totalHoursStr} label="Across all active students" delta="this term" fill={62} icon="doc" />
          <DashKpiCard eyebrow="TOTAL" value={total} label={pcFilter ? 'Students assigned' : 'Across all cohorts'} delta={pcFilter ? `${complete} complete` : `${activeHosts} active host partners`} fill={total ? Math.round(complete / total * 100) : 0} icon="hosts" />
        </div>
      </div>
    </div>
  );
}

function DashKpiCard({ eyebrow, value, label, delta, fill, icon }) {
  const iconPath = {
    students: <g><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="7" r="2.5"/><path d="M16 14c2.8 0 5 2.2 5 5"/></g>,
    reports:  <g><path d="M3 20h18"/><path d="M6 20V10M11 20V4M16 20v-7"/></g>,
    doc:      <g><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h4"/></g>,
    hosts:    <g><path d="M3 21V8l9-5 9 5v13"/><path d="M9 21v-7h6v7"/><path d="M3 21h18"/></g>,
  };
  return (
    <div className="h-stat-card">
      <div className="h-stat-top">
        <span className="h-stat-eyebrow" style={{ color: AMBER }}>{eyebrow}</span>
        <span className="dash-stat-ico"><DI d={iconPath[icon]} size={14} /></span>
      </div>
      <div className="h-stat-num">{value}</div>
      <div className="h-stat-label">{label}</div>
      <div className="h-stat-bar"><div className="h-stat-bar-fill" style={{ width: `${Math.min(100, fill || 0)}%`, background: AMBER }} /></div>
      <div className="dash-stat-delta"><span className="dash-delta-pill">{delta}</span></div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Widget shell
═══════════════════════════════════════ */
function Widget({ title, sub, action, children, noPad = false }) {
  return (
    <section className="wgt">
      <header className="wgt-head">
        <div>
          <h3 className="wgt-title">{title}</h3>
          {sub && <div className="wgt-sub">{sub}</div>}
        </div>
        {action}
      </header>
      <div className={noPad ? 'wgt-body no-pad' : 'wgt-body'}>{children}</div>
    </section>
  );
}

/* ═══════════════════════════════════════
   Placement Trend Chart (SVG area chart)
═══════════════════════════════════════ */
function PlacementChart({ data }) {
  const [hover, setHover] = useState(null);
  const W = 720, H = 220, padL = 32, padR = 12, padT = 10, padB = 26;
  const maxVal = Math.max(...data.map(d => d.placed), 1);
  const stepX = (W - padL - padR) / Math.max(data.length - 1, 1);
  const yFor = v => padT + (H - padT - padB) * (1 - v / maxVal);
  const xFor = i => padL + i * stepX;

  const linePath = key => data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(d[key]).toFixed(1)}`).join(' ');
  const areaPath = key => `${linePath(key)} L ${xFor(data.length - 1).toFixed(1)} ${H - padB} L ${xFor(0).toFixed(1)} ${H - padB} Z`;

  const hasData = data.some(d => d.placed > 0 || d.completed > 0);

  if (!hasData) {
    return (
      <div className="chart-wrap">
        <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', fontSize: 13, fontStyle: 'italic' }}>
          Placement data will appear here once students are assigned practicum start dates.
        </div>
      </div>
    );
  }

  return (
    <div className="chart-wrap">
      <div className="chart-legend">
        <span><i style={{ background: AMBER }} /> Placements started</span>
        <span><i style={{ background: '#0F2A3D' }} /> Completions</span>
        <div className="chart-range">
          <button>4w</button><button className="active">12w</button><button>YTD</button>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="dg1" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={AMBER} stopOpacity="0.28" />
            <stop offset="100%" stopColor={AMBER} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="dg2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0F2A3D" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#0F2A3D" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, maxVal / 2, maxVal].map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={yFor(t)} x2={W - padR} y2={yFor(t)} stroke="#EEF1F5" strokeDasharray="2 4" />
            <text x={padL - 6} y={yFor(t) + 4} textAnchor="end" fontSize="9.5" fill="#8C9DAE">{Math.round(t)}</text>
          </g>
        ))}
        <path d={areaPath('placed')} fill="url(#dg1)" />
        <path d={areaPath('completed')} fill="url(#dg2)" />
        <path d={linePath('placed')} fill="none" stroke={AMBER} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d={linePath('completed')} fill="none" stroke="#0F2A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 3" />
        {data.map((d, i) => (
          <g key={i}>
            <text x={xFor(i)} y={H - padB + 14} textAnchor="middle" fontSize="9.5" fill="#8C9DAE">{d.w}</text>
            <circle cx={xFor(i)} cy={yFor(d.placed)} r={hover === i ? 5 : 3} fill={AMBER} stroke="white" strokeWidth="1.5" />
            <rect x={xFor(i) - 14} y="0" width="28" height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
          </g>
        ))}
        {hover != null && (
          <g pointerEvents="none">
            <line x1={xFor(hover)} y1={padT} x2={xFor(hover)} y2={H - padB} stroke="#0B1F2E" strokeOpacity="0.12" />
            <g transform={`translate(${Math.min(xFor(hover) + 8, W - 130)}, ${Math.max(yFor(data[hover].placed) - 42, padT)})`}>
              <rect width="118" height="48" rx="8" fill="#0B1F2E" />
              <text x="10" y="16" fontSize="9.5" fill="#A6B7C7">{data[hover].w}</text>
              <text x="10" y="30" fontSize="12" fill="white" fontWeight="600">{data[hover].placed} placed</text>
              <text x="10" y="42" fontSize="10" fill={AMBER}>{data[hover].completed} completed</text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════
   Student Table
═══════════════════════════════════════ */
function DashStudentTable({ students, navigate, PROGRAM_NAMES }) {
  if (students.length === 0)
    return <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, fontStyle: 'italic' }}>No students yet.</div>;

  return (
    <div className="dsb-table">
      <div className="dsb-head">
        <div>Student</div><div>Program · Campus</div><div>Status</div>
        <div>Hours</div><div>PC</div><div />
      </div>
      {students.map(s => {
        const st = STATUS_CFG[s.status] || STATUS_CFG.active;
        const pct = s.hours_required ? Math.min(100, Math.round((parseFloat(s.hours_logged) || 0) / s.hours_required * 100)) : 0;
        const initials = [s.first_name, s.last_name].map(n => n?.[0] || '').join('').toUpperCase();
        const progName = PROGRAM_NAMES[s.program_code] || s.program_name || s.program_code || '—';
        return (
          <div key={s.id} className="dsb-row" onClick={() => navigate(`/students/${s.id}`)}>
            <div className="dsb-student">
              <div className="dsb-avatar">{initials}</div>
              <div>
                <div className="dsb-name">{s.first_name} {s.last_name}</div>
                <div className="dsb-sub">{s.email}</div>
              </div>
            </div>
            <div className="dsb-prog">
              <div style={{ color: 'var(--ink)', fontWeight: 500, fontSize: 12.5, lineHeight: 1.3 }}>{progName}</div>
              <div className="dsb-sub">{s.campus || '—'}{s.host_name ? ` · ${s.host_name}` : ''}</div>
            </div>
            <div>
              <span className="status-pill" style={{ background: st.bg, color: st.color, fontSize: 11 }}>
                <span className="status-dot" style={{ background: st.dot }} /> {st.label}
              </span>
            </div>
            <div className="dsb-hours">
              <div className="dsb-hours-bar">
                <div className="dsb-hours-fill" style={{ width: pct + '%', background: st.bar }} />
              </div>
              <div className="dsb-sub">{Math.round(parseFloat(s.hours_logged) || 0)} / {s.hours_required || 0} hrs</div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{s.pc_name?.split(' ')[0] || '—'}</div>
            <button className="dsb-view">View <DI d="M5 12h14M13 6l6 6-6 6" size={12} /></button>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════
   Status Breakdown
═══════════════════════════════════════ */
function DashStatusBreakdown({ byStatus, max }) {
  return (
    <div className="dsb-status">
      {byStatus.map(s => (
        <div key={s.key} className="dsb-sb-row">
          <span className="status-pill" style={{ background: s.bg, color: s.color, fontSize: 11, minWidth: 96 }}>
            <span className="status-dot" style={{ background: s.dot }} /> {s.label}
          </span>
          <div className="dsb-sb-bar">
            <div className="dsb-sb-fill" style={{ width: `${(s.count / max) * 100}%`, background: s.bar }} />
          </div>
          <div className="dsb-sb-count">{s.count}</div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   Tasks
═══════════════════════════════════════ */
function DashTasks({ tasks, navigate }) {
  const [done, setDone] = useState(new Set());
  const toggle = id => setDone(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const typeIcon = {
    agreement: <DI d={<g><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13h6M9 17h4"/></g>} size={14} />,
    placement: <DI d={<g><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="7" r="2.5"/><path d="M16 14c2.8 0 5 2.2 5 5"/></g>} size={14} />,
    doc:       <DI d={<g><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></g>} size={14} />,
    email:     <DI d={<g><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></g>} size={14} />,
    hours:     <DI d={<g><path d="M3 20h18"/><path d="M6 20V10M11 20V4M16 20v-7"/></g>} size={14} />,
  };
  const priColor = { high: '#D14343', med: '#F5A623', low: '#5C7388' };

  return (
    <ul className="dsb-tasks">
      {tasks.map(t => {
        const isDone = done.has(t.id);
        return (
          <li key={t.id} className={`dsb-task${isDone ? ' done' : ''}`}>
            <button
              className="dsb-check"
              onClick={() => toggle(t.id)}
              style={isDone ? { background: AMBER, borderColor: AMBER } : {}}
            >
              {isDone && <svg viewBox="0 0 16 16" width="10" height="10" fill="none" stroke="#0B1F2E" strokeWidth="2.5" strokeLinecap="round"><path d="M3 8.5l3 3 7-7" /></svg>}
            </button>
            <div className="dsb-task-ico">{typeIcon[t.type] || typeIcon.doc}</div>
            <div className="dsb-task-body" onClick={() => t.href && navigate(t.href)} style={t.href ? { cursor: 'pointer' } : {}}>
              <div className="dsb-task-title">{t.title}</div>
              <div className="dsb-task-sub">{t.subtitle}</div>
            </div>
            <span className="dsb-task-pri" style={{ background: priColor[t.priority] || priColor.low }} />
          </li>
        );
      })}
    </ul>
  );
}

/* ═══════════════════════════════════════
   Hosts Needing Attention
═══════════════════════════════════════ */
function DashHostsAttention({ items, navigate }) {
  return (
    <ul className="dsb-attn">
      {items.map((h, i) => (
        <li key={i} className="dsb-attn-row">
          <span className="dsb-attn-dot" style={{ background: h.level === 'red' ? '#D14343' : '#F5A623' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{h.reason}</div>
          </div>
          <button className="dsb-attn-act" onClick={() => navigate(`/hosts`)}>
            <DI d="M5 12h14M13 6l6 6-6 6" size={14} />
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ═══════════════════════════════════════
   Recent Activity Feed
═══════════════════════════════════════ */
const TRIGGER_LABELS = {
  host_nurture:        { label: 'sent partner check-in to',    kind: 'email' },
  agreement_sent:      { label: 'sent site agreement to',      kind: 'doc' },
  practicum_start:     { label: 'started practicum at',        kind: 'match' },
  offboarding_started: { label: 'began offboarding for',       kind: 'done' },
};
const KIND_COLOR = { doc: AMBER, match: '#1F87E5', hours: '#8A5BD0', host: '#1F8A5B', done: '#1F8A5B', email: '#5C7388' };

function DashActivity({ items }) {
  const rows = (items || []).slice(0, 3);
  if (rows.length === 0)
    return <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>No recent activity recorded yet.</p>;

  return (
    <ul className="dsb-act">
      {rows.map((a, i) => {
        const cfg = TRIGGER_LABELS[a.trigger_type] || { label: a.trigger_type?.replace(/_/g, ' ') || 'sent email to', kind: 'email' };
        const target = a.host_name || a.student_name || a.recipient;
        const ts = a.sent_at ? timeAgo(a.sent_at) : '';
        return (
          <li key={i} className="dsb-act-row">
            <span className="dsb-act-dot" style={{ background: KIND_COLOR[cfg.kind] || AMBER }} />
            <div>
              <div className="dsb-act-text">
                <strong>System</strong> {cfg.label} <span className="dsb-act-target">{target}</span>
              </div>
              <div className="dsb-act-time">{ts}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

/* ═══════════════════════════════════════
   Program Split
═══════════════════════════════════════ */
function DashProgramSplit({ programs }) {
  if (!programs || programs.length === 0) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>
        Program breakdown will appear once students are enrolled.
      </p>
    );
  }
  const max = Math.max(...programs.map(p => p.students), 1);
  return (
    <div className="dsb-prog-split">
      {programs.map((p, i) => {
        const sPct = (p.students / max) * 100;
        const pPct = p.students ? (p.placements / p.students) * 100 : 0;
        return (
          <div key={i} className="dsb-prog-row">
            <div className="dsb-prog-meta">
              <span className="dsb-prog-swatch" style={{ background: p.color }} />
              <span className="dsb-prog-name">{p.name}</span>
              <span className="dsb-prog-nums"><strong>{p.placements}</strong> / {p.students}</span>
            </div>
            <div className="dsb-prog-bar">
              <div className="dsb-prog-bg" style={{ width: sPct + '%', background: p.color, opacity: 0.18 }} />
              <div className="dsb-prog-fg" style={{ width: (sPct * pPct / 100) + '%', background: p.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════
   Upcoming (cohort dates)
═══════════════════════════════════════ */
function DashUpcoming({ dates, students }) {
  const now = new Date();

  const upcoming = useMemo(() => {
    const filtered = (dates || [])
      .filter(d => d !== 'no-date')
      .map(d => new Date(d))
      .filter(d => d >= now)
      .sort((a, b) => a - b)
      .slice(0, 5);

    return filtered.map(d => {
      const cohortStudents = students.filter(s => {
        const sd = s.cohort_start_date || s.practicum_start_date;
        if (!sd) return false;
        return new Date(sd).toDateString() === d.toDateString();
      });

      const diffDays = Math.ceil((d - now) / 86400000);
      const isToday    = diffDays === 0;
      const isTomorrow = diffDays === 1;
      const thisWeek   = diffDays <= 7;

      const dayLabel = isToday
        ? 'TODAY'
        : isTomorrow
        ? 'TOMORROW'
        : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();

      const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

      const programs = [...new Set(cohortStudents.map(s => s.program_name || s.program_code).filter(Boolean))];
      const title = programs.length > 0
        ? `Cohort intake — ${programs[0]}${programs.length > 1 ? ` +${programs.length - 1} more` : ''}`
        : `Cohort start date`;

      return {
        date: d, dayLabel, timeStr,
        title, count: cohortStudents.length,
        highlight: isToday || isTomorrow,
      };
    });
  }, [dates, students, now]);

  if (upcoming.length === 0) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', margin: 0 }}>
        No upcoming cohort dates this week.
      </p>
    );
  }

  return (
    <ul className="dsb-upcoming">
      {upcoming.map((item, i) => (
        <li key={i} className={`dsb-up-row${item.highlight ? ' highlight' : ''}`}>
          <div className="dsb-up-date">
            <div className="dsb-up-day">{item.dayLabel}</div>
            <div className="dsb-up-time">{item.timeStr}</div>
          </div>
          <div className="dsb-up-body">
            <div className="dsb-up-title">{item.title}</div>
            {item.count > 0 && <div className="dsb-up-sub">{item.count} student{item.count !== 1 ? 's' : ''}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ═══════════════════════════════════════
   Icon helper
═══════════════════════════════════════ */
function DI({ d, size = 18, stroke = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  );
}
